// Persist the user's last in-app route so a WKWebView content-process
// restart (e.g. OOM from a large image) lands them back where they were
// instead of bouncing to the marketing home page.
//
// Storage: sessionStorage (survives content-process restarts within the
// same app launch; cleared on full app kill, which is correct — a true
// cold launch should respect the normal routing).

const KEY = "roava:last-route";
const TTL_MS = 30 * 60 * 1000; // 30 min — defensive upper bound

type Saved = { path: string; ts: number };

// Routes we never want to restore TO (would create loops or land users on
// modal/full-screen flows that no longer make sense).
const SKIP_PREFIXES = [
  "/",            // marketing landing (handled separately)
  "/login",
  "/face-id-intro",
  "/join",
  "/offline-control",
  "/follow",
  "/account-deletion",
  "/privacy",
  "/terms",
  "/support",
];

function isRestorable(path: string): boolean {
  if (!path || path === "/" ) return false;
  // Only restore authenticated app routes (everything else is marketing/auth).
  // The authenticated routes don't share a single URL prefix, so we allowlist
  // by exclusion: skip the known non-app paths above.
  for (const p of SKIP_PREFIXES) {
    if (path === p || path.startsWith(p + "/")) return false;
  }
  return true;
}

export function saveLastRoute(path: string): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    if (!isRestorable(path)) return;
    const v: Saved = { path, ts: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export function getLastRoute(): string | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Saved;
    if (!v?.path || typeof v.ts !== "number") return null;
    if (Date.now() - v.ts > TTL_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    if (!isRestorable(v.path)) return null;
    return v.path;
  } catch {
    return null;
  }
}

export function clearLastRoute(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
